import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { onAuthStateChanged } from 'firebase/auth';
import { ref, uploadBytes, getDownloadURL } from 'firebase/storage';
import { doc, getDoc, setDoc } from 'firebase/firestore';
import { auth, storage, db } from '../config/firebase';
import './AdminVisual.css';

const AdminVisual = () => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [isAdmin, setIsAdmin] = useState(false);
  const [images, setImages] = useState({});
  const [uploading, setUploading] = useState({});
  const [toast, setToast] = useState(null);
  const navigate = useNavigate();

  const showToast = (message, type) => {
    setToast({ message, type });
    setTimeout(() => setToast(null), 3000);
  };

  useEffect(() => {
    const unsubscribe = onAuthStateChanged(auth, async (currentUser) => {
      if (!currentUser) {
        navigate('/employee');
        return;
      }
      
      // Check if user is admin
      try {
        const userDocRef = doc(db, 'users', currentUser.uid);
        const userDoc = await getDoc(userDocRef);
        if (userDoc.exists()) {
          const userData = userDoc.data();
          const userIsAdmin = userData.role === 'admin' || userData.role === 'administrator';
          setIsAdmin(userIsAdmin);
          
          if (!userIsAdmin) {
            showToast('Access denied. Admin privileges required.', 'error');
            setTimeout(() => navigate('/'), 2000);
            return;
          }
        } else {
          // User document doesn't exist, deny access
          showToast('Access denied. Admin privileges required.', 'error');
          setTimeout(() => navigate('/'), 2000);
          return;
        }
      } catch (error) {
        console.error('Error checking admin status:', error);
        showToast('Error verifying access. Please try again.', 'error');
        setTimeout(() => navigate('/'), 2000);
        return;
      }
      
      setUser(currentUser);
      setLoading(false);
      loadImages();
    });

    return () => unsubscribe();
  }, [navigate]);

  const loadImages = async () => {
    try {
      const docRef = doc(db, 'site_images', 'images');
      const docSnap = await getDoc(docRef);
      if (docSnap.exists()) {
        setImages(docSnap.data());
      } else {
        // Initialize with default structure
        const defaultImages = {
          home_hero: '',
          milk_section: '',
          pharmapp_hero: '',
          investors_hero: '',
          contact_hero: '',
          farm_gallery: []
        };
        await setDoc(docRef, defaultImages);
        setImages(defaultImages);
      }
    } catch (error) {
      console.error('Error loading images:', error);
      showToast('Error loading images', 'error');
    }
  };

  const handleImageUpload = async (imageKey, file) => {
    if (!file) return;

    setUploading({ ...uploading, [imageKey]: true });

    try {
      // Upload to Firebase Storage
      const storageRef = ref(storage, `site_images/${imageKey}/${Date.now()}_${file.name}`);
      await uploadBytes(storageRef, file);
      const downloadURL = await getDownloadURL(storageRef);

      // Update Firestore
      const docRef = doc(db, 'site_images', 'images');
      const newImages = { ...images, [imageKey]: downloadURL };
      await setDoc(docRef, newImages, { merge: true });
      setImages(newImages);

      showToast('Image updated successfully!', 'success');
    } catch (error) {
      console.error('Error uploading image:', error);
      showToast('Error uploading image', 'error');
    } finally {
      setUploading({ ...uploading, [imageKey]: false });
    }
  };

  const imageSlots = [
    { key: 'home_hero', label: 'Home Hero Image', description: 'Main hero background on homepage' },
    { key: 'milk_section', label: 'Premium Milk Hero', description: 'Hero image for premium milk page' },
    { key: 'pharmapp_hero', label: 'PharmApp Hero', description: 'Hero image for PharmApp page' },
    { key: 'investors_hero', label: 'Investors Hero', description: 'Hero image for investors page' },
    { key: 'contact_hero', label: 'Contact Hero', description: 'Hero image for contact page' }
  ];

  if (loading) {
    return (
      <div className="admin-visual-page">
        <div className="loading">Verifying access...</div>
      </div>
    );
  }

  if (!isAdmin) {
    return (
      <div className="admin-visual-page">
        <div className="loading">Access denied. Admin privileges required.</div>
      </div>
    );
  }

  return (
    <div className="admin-visual-page">
      <div className="admin-header">
        <h1>Visual Editor</h1>
        <p>Manage site images and photos</p>
      </div>

      {toast && (
        <div className={`toast toast-${toast.type}`}>
          {toast.message}
        </div>
      )}

      <div className="admin-container">
        <div className="images-grid">
          {imageSlots.map((slot) => (
            <div key={slot.key} className="image-slot card card-gold">
              <h3>{slot.label}</h3>
              <p className="slot-description">{slot.description}</p>
              
              <div className="image-preview">
                {images[slot.key] ? (
                  <img src={images[slot.key]} alt={slot.label} />
                ) : (
                  <div className="no-image">No image set</div>
                )}
              </div>

              <div className="upload-controls">
                <label className="upload-btn">
                  {uploading[slot.key] ? 'Uploading...' : 'Replace Photo'}
                  <input
                    type="file"
                    accept="image/*"
                    onChange={(e) => {
                      const file = e.target.files[0];
                      if (file) handleImageUpload(slot.key, file);
                    }}
                    disabled={uploading[slot.key]}
                    style={{ display: 'none' }}
                  />
                </label>
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
};

export default AdminVisual;

